import React, { useState, useEffect, useCallback } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
// Produit fourni par le client : désactivé pour le moment
// import { Checkbox } from '../../../components/ui/Checkbox';
import { supabase } from '../../../lib/supabase';
import StepActions from './StepActions';
import {
  // Produit fourni par le client : désactivé pour le moment
  // buildServiceWithProductChoice,
  // getServiceProductPrice,
  buildServiceWithMaterial,
  buildOtherMaterialQuoteService,
  getDefaultMaterial,
  getServicePriceWithoutMaterial,
} from '../serviceProductPricing';

const ServiceStep = ({ data, onUpdate, onNext, onBack, isActive = true, stepIndex = 3, showBack = true }) => {
  const [selectedService, setSelectedService] = useState(data?.service || null);
  const [error, setError] = useState('');
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [materialsByService, setMaterialsByService] = useState({});
  const [selectedMaterials, setSelectedMaterials] = useState({});
  const [materialPickerServiceId, setMaterialPickerServiceId] = useState(null);

  // Produit fourni par le client : désactivé pour le moment
  // const [clientProvidesProduct, setClientProvidesProduct] = useState({});
  // const [productPopupService, setProductPopupService] = useState(null);
  // const productPopupRef = useRef(null);
  //
  // const closeProductPopup = useCallback(() => {
  //   setProductPopupService(null);
  // }, []);
  //
  // const openProductPopup = (service, event) => {
  //   event?.stopPropagation();
  //   event?.preventDefault();
  //   setProductPopupService(service);
  // };
  //
  // const serviceHasProductDetails = (service) => {
  //   const hasDescription = Boolean(`${service?.description_produit || ''}`.trim());
  //   const hasImage = Boolean(`${service?.image_produit || ''}`.trim());
  //   return hasDescription || hasImage;
  // };
  //
  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (productPopupRef.current && !productPopupRef.current.contains(event.target)) {
  //       closeProductPopup();
  //     }
  //   };
  //
  //   if (productPopupService) {
  //     document.addEventListener('mousedown', handleClickOutside);
  //     return () => {
  //       document.removeEventListener('mousedown', handleClickOutside);
  //     };
  //   }
  // }, [productPopupService, closeProductPopup]);

  const isFixedPriceService = (service) =>
    service?.PricingModel === 'prix fixe' || service?.PricingModel === 'fixed';

  const getServiceMaterials = (service, materialsMap = materialsByService) =>
    service?.necessite_materiel && isFixedPriceService(service) ? materialsMap[service.id] || [] : [];

  const getMaterialForService = (service, materialsMap = materialsByService, selection = selectedMaterials) =>
    getServiceMaterials(service, materialsMap).length > 0 ? selection[service.id] || null : null;

  const closeMaterialPicker = useCallback(() => {
    setMaterialPickerServiceId(null);
  }, []);

  useEffect(() => {
    if (!materialPickerServiceId) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeMaterialPicker();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [materialPickerServiceId, closeMaterialPicker]);

  useEffect(() => {
    fetchServices();
  }, [data?.problemId, data?.equipment?.id]);

  useEffect(() => {
    if (data?.service) {
      setSelectedService(data.service);
      if (data.service.selectedMaterial) {
        setSelectedMaterials((prev) => ({
          ...prev,
          [data.service.id]: data.service.selectedMaterial,
        }));
      }
      return;
    }

    if (services.length === 1) {
      const onlyService = services[0];
      setSelectedService(buildServiceWithMaterial(onlyService, getMaterialForService(onlyService)));
      return;
    }

    setSelectedService(null);
  }, [data?.service?.id, data?.problemId, data?.service?.selectedMaterial?.id, services]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setFetchError(null);

      const problemId = data?.problemId;
      const equipmentId = data?.equipment?.id;

      if (!problemId && !equipmentId) {
        setServices([]);
        setLoading(false);
        return;
      }

      let query = supabase
        .from('service')
        .select('*')
        .eq('actif', true);

      // Si on a un problemId, filtrer par problème
      if (problemId) {
        query = query.eq('problem', problemId);
      } 
      // Sinon, si on a un equipmentId, récupérer les services via les problèmes
      else if (equipmentId) {
        // D'abord récupérer les problèmes de l'équipement
        const { data: problemsData, error: problemsError } = await supabase
          .from('problem')
          .select('id')
          .eq('equipment', equipmentId)
          .eq('actif', true);

        if (problemsError) {
          console.error('Problems error:', problemsError);
          throw problemsError;
        }

        if (problemsData && problemsData.length > 0) {
          const problemIds = problemsData.map(p => p.id);
          query = query.in('problem', problemIds);
        } else {
          setServices([]);
          setLoading(false);
          return;
        }
      }

      const { data: servicesData, error: servicesError } = await query
        .order('order', { ascending: true, nullsLast: true })
        .order('name', { ascending: true });

      if (servicesError) {
        console.error('Services error:', servicesError);
        throw servicesError;
      }

      console.log('Services data from DB:', servicesData);

      // Transform data to match component structure
      const transformedServices = (servicesData || []).map(service => ({
        id: service.id,
        name: service.name || 'Sans nom',
        description: service.description || '',
        basePrice: service.basePrice || 0,
        price: service.basePrice || 0,
        duration: service.estimatedDuration ? `${service.estimatedDuration} min` : 'Variable',
        PricingModel: service.PricingModel || service.pricing_model || 'prix fixe',
        icon: getServiceIcon(service.name),
        features: buildServiceFeatures(service),
        guarantee: service.Guarantee,
        tva: service.TVA,
        requiredSkills: service.requiredSkills,
        landModifications: service.landModifications,
        clientValidation: service.clientValidation,
        necessite_materiel: service.necessite_materiel !== false,
        produit_fournissable: Boolean(service.produit_fournissable),
        prix_avec_produit: service.prix_avec_produit,
        prix_sans_produit: service.prix_sans_produit,
        description_produit: service.description_produit || '',
        image_produit: service.image_produit || '',
        note: service.PricingModel !== 'prix fixe' && service.PricingModel !== 'fixed' 
          ? 'Sur devis : diagnostic rapide, devis net avant toute action' 
          : null
      }));

      console.log('Transformed services:', transformedServices);

      const serviceIdsNeedingMaterial = transformedServices
        .filter((service) => service.necessite_materiel)
        .map((service) => service.id);

      const materialsMap = {};
      if (serviceIdsNeedingMaterial.length > 0) {
        const { data: materialsData, error: materialsError } = await supabase
          .from('service_matriel')
          .select('*')
          .in('service_id', serviceIdsNeedingMaterial)
          .order('created_at', { ascending: true });

        if (materialsError) {
          console.error('Materials error:', materialsError);
        } else {
          (materialsData || []).forEach((material) => {
            if (!materialsMap[material.service_id]) materialsMap[material.service_id] = [];
            materialsMap[material.service_id].push(material);
          });
        }
      }

      const initialMaterials = {};
      serviceIdsNeedingMaterial.forEach((serviceId) => {
        const list = materialsMap[serviceId] || [];
        const previous = data?.service?.id === serviceId ? data.service.selectedMaterial : null;
        initialMaterials[serviceId] =
          (previous && list.find((material) => material.id === previous.id)) || getDefaultMaterial(list);
      });

      setMaterialsByService(materialsMap);
      setSelectedMaterials(initialMaterials);
      setServices(transformedServices);

      if (transformedServices.length === 1) {
        const onlyService = transformedServices[0];
        setSelectedService(
          buildServiceWithMaterial(onlyService, getMaterialForService(onlyService, materialsMap, initialMaterials))
        );
        setError('');
      } else if (
        data?.service?.id &&
        !transformedServices.some((service) => service.id === data.service.id)
      ) {
        setSelectedService(null);
      }
    } catch (err) {
      console.error('Error fetching services:', err);
      setFetchError(err.message || 'Erreur lors du chargement des services');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to determine icon based on service name
  const getServiceIcon = (name) => {
    const nameLower = (name || '').toLowerCase();
    if (nameLower.includes('débouchage') || nameLower.includes('debouchage')) return 'Wrench';
    if (nameLower.includes('fuite') || nameLower.includes('réparation')) return 'Droplet';
    if (nameLower.includes('robinet') || nameLower.includes('remplacement')) return 'Settings';
    if (nameLower.includes('diagnostic') || nameLower.includes('inspection')) return 'Search';
    if (nameLower.includes('installation') || nameLower.includes('équipement')) return 'Package';
    if (nameLower.includes('urgence') || nameLower.includes('dépannage')) return 'AlertCircle';
    return 'Wrench'; // Default icon
  };

  // Helper function to build features array from service data
  const buildServiceFeatures = (service) => {
    const features = [];

    if (service.PricingModel === 'prix fixe' || service.PricingModel === 'fixed') {
      features.push('Prix connu à l’avance');
    } else {
      features.push('Devis personnalisé');
    }
    if (service.estimatedDuration && service.estimatedDuration <= 60) {
      features.push('Intervention rapide et soignée');
    }
    if (service.Guarantee) {
      features.push(`Garantie ${service.Guarantee} mois`);
    }
    
    
    
    if (service.requiredSkills) {
      features.push('Technicien qualifié');
    }
    
    if (service.clientValidation) {
      features.push('Validation client requise');
    }
    
    return features.length > 0 ? features : ['Service professionnel'];
  };

  // Produit fourni par le client : désactivé pour le moment
  // const handleClientProvidesProductChange = (service, checked, event) => {
  //   event?.stopPropagation();
  //   setClientProvidesProduct((prev) => ({ ...prev, [service.id]: checked }));
  //
  //   if (selectedService?.id === service.id) {
  //     const updatedService = buildServiceWithProductChoice(service, checked);
  //     setSelectedService(updatedService);
  //     onUpdate({ service: updatedService });
  //   }
  // };

  const handleMaterialSelect = (service, material) => {
    setSelectedMaterials((prev) => ({ ...prev, [service.id]: material }));

    if (selectedService?.id === service.id) {
      const updatedService = buildServiceWithMaterial(service, material);
      setSelectedService(updatedService);
      if (data?.service?.id === service.id) {
        onUpdate({ service: updatedService });
      }
    }
    closeMaterialPicker();
  };

  const isOtherMaterialSelected = (service) =>
    selectedService?.id === service?.id && Boolean(selectedService?.otherMaterialRequested);

  const handleOtherMaterialSelect = (service) => {
    const quoteService = buildOtherMaterialQuoteService(service);
    setSelectedService(quoteService);
    setError('');
    if (data?.service?.id === service.id) {
      onUpdate({ service: quoteService });
    }
    closeMaterialPicker();
  };

  const handleServiceSelect = (service) => {
    if (isOtherMaterialSelected(service)) {
      setError('');
      onUpdate({ service: selectedService });
      onNext(true);
      return;
    }
    const serviceWithMaterial = buildServiceWithMaterial(service, getMaterialForService(service));
    setSelectedService(serviceWithMaterial);
    setError('');
    onUpdate({ service: serviceWithMaterial });
    onNext(true);
  };

  const handleSubmit = () => {
    // Vérifier si un service est sélectionné
    if (!selectedService) {
      setError('Veuillez sélectionner un service pour continuer');
      return;
    }

    console.log('Service soumis:', selectedService);
    console.log('PricingModel valeur:', selectedService.PricingModel);

    // Mettre à jour les données dans le parent
    onUpdate({
      service: selectedService.otherMaterialRequested
        ? selectedService
        : buildServiceWithMaterial(selectedService, getMaterialForService(selectedService)),
    });
onNext(true);
    // Vérifier le modèle de tarification (gère plusieurs formats possibles)
    
  };

  // Loading state
  if (loading) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="bg-card rounded-lg shadow-md p-6 lg:p-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Chargement des services...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="bg-card rounded-lg shadow-md p-6 lg:p-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} color="var(--color-error)" className="mx-auto mb-4" />
              <p className="text-error font-medium mb-4">{fetchError}</p>
              <Button onClick={fetchServices} variant="outline">
                Réessayer
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (services.length === 0) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="bg-card rounded-lg shadow-md p-6 lg:p-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Icon name="Package" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">Aucun service disponible pour ce problème.</p>
              <StepActions isActive={isActive} showBack={showBack}>
                <Button onClick={onBack} variant="outline">
                  Retour
                </Button>
              </StepActions>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Produit fourni par le client : désactivé pour le moment
  // const ProductDetailsPopup = () => {
  //   if (!productPopupService) return null;
  //
  //   const description = `${productPopupService.description_produit || ''}`.trim();
  //   const imageUrl = `${productPopupService.image_produit || ''}`.trim();
  //
  //   return (
  //     <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
  //       <div
  //         ref={productPopupRef}
  //         className="bg-card rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden"
  //         role="dialog"
  //         aria-modal="true"
  //         aria-labelledby="product-popup-title"
  //       >
  //         <div className="flex items-center justify-between p-5 border-b border-border">
  //           <div className="flex items-center gap-3">
  //             <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
  //               <Icon name="Package" size={20} className="text-primary" />
  //             </div>
  //             <div>
  //               <h3 id="product-popup-title" className="text-lg font-semibold text-foreground">
  //                 Matériel fourni
  //               </h3>
  //               <p className="text-xs text-muted-foreground">
  //                 {productPopupService.name}
  //               </p>
  //             </div>
  //           </div>
  //           <button
  //             type="button"
  //             onClick={closeProductPopup}
  //             className="p-2 hover:bg-muted rounded-lg transition-colors"
  //             aria-label="Fermer"
  //           >
  //             <Icon name="X" size={20} className="text-muted-foreground" />
  //           </button>
  //         </div>
  //
  //         <div className="p-5 overflow-y-auto max-h-[calc(90vh-140px)] space-y-5">
  //           {description ? (
  //             <div>
  //               <h4 className="text-sm font-semibold text-foreground mb-2">Description</h4>
  //               <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
  //                 {description}
  //               </p>
  //             </div>
  //           ) : (
  //             <div className="text-center py-4">
  //               <Icon name="FileText" size={28} className="text-muted-foreground mx-auto mb-2" />
  //               <p className="text-sm text-muted-foreground">
  //                 Aucune description disponible pour ce matériel.
  //               </p>
  //             </div>
  //           )}
  //
  //           {imageUrl ? (
  //             <div>
  //               <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
  //                 <Icon name="Image" size={16} className="text-primary" />
  //                 Illustration
  //               </h4>
  //               <div className="rounded-lg overflow-hidden border border-border bg-muted/20 p-3 flex items-center justify-center">
  //                 <img
  //                   src={imageUrl}
  //                   alt="Matériel fourni"
  //                   className="max-h-64 w-auto max-w-full object-contain"
  //                   onError={(e) => {
  //                     e.target.src = '/placeholder-image.jpg';
  //                   }}
  //                 />
  //               </div>
  //             </div>
  //           ) : null}
  //         </div>
  //
  //         <div className="flex justify-end p-5 border-t border-border bg-muted/20">
  //           <Button variant="default" size="sm" onClick={closeProductPopup}>
  //             Fermer
  //           </Button>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // };

  const MaterialPickerPopup = () => {
    const service = services.find((s) => s.id === materialPickerServiceId);
    if (!service) return null;

    const materials = materialsByService[service.id] || [];
    const otherSelected = isOtherMaterialSelected(service);
    const currentMaterial = otherSelected ? null : selectedMaterials[service.id];
    const prixSansMateriel = getServicePriceWithoutMaterial(service);

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        onClick={closeMaterialPicker}
      >
        <div
          className="bg-card rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden"
          role="dialog"
          aria-modal="true"
          aria-labelledby="material-picker-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-5 border-b border-border">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Package" size={20} className="text-primary" />
              </div>
              <div className="min-w-0">
                <h3 id="material-picker-title" className="text-lg font-semibold text-foreground">
                  Choisir le matériel
                </h3>
                <p className="text-xs text-muted-foreground truncate">{service.name}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={closeMaterialPicker}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              aria-label="Fermer"
            >
              <Icon name="X" size={20} className="text-muted-foreground" />
            </button>
          </div>

          <div className="p-5 overflow-y-auto space-y-3" role="radiogroup" aria-labelledby="material-picker-title">
            {materials.map((material) => {
              const isSelected = currentMaterial?.id === material.id;
              const materialPrice = Number(material.prix) || 0;
              return (
                <button
                  key={material.id}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => handleMaterialSelect(service, material)}
                  className={`w-full flex items-center gap-4 p-3 rounded-lg border-2 text-left transition-colors ${
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50 bg-card'
                  }`}
                >
                  <div className="w-20 h-20 rounded-md overflow-hidden border border-border bg-background flex items-center justify-center flex-shrink-0">
                    {material.image_url ? (
                      <img
                        src={material.image_url}
                        alt={material.nom}
                        className="max-h-full max-w-full object-contain"
                        onError={(e) => {
                          e.target.src = '/placeholder-image.jpg';
                        }}
                      />
                    ) : (
                      <Icon name="Package" size={24} className="text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-foreground">{material.nom}</p>
                      {material.is_default && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                          Recommandé
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      + {materialPrice.toFixed(2)} € HT
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Total prestation : {(prixSansMateriel + materialPrice).toFixed(2)} € HT
                    </p>
                  </div>
                  <Icon
                    name={isSelected ? 'CheckCircle' : 'Circle'}
                    size={22}
                    className={isSelected ? 'text-primary flex-shrink-0' : 'text-muted-foreground flex-shrink-0'}
                  />
                </button>
              );
            })}

            <button
              type="button"
              role="radio"
              aria-checked={otherSelected}
              onClick={() => handleOtherMaterialSelect(service)}
              className={`w-full flex items-center gap-4 p-3 rounded-lg border-2 border-dashed text-left transition-colors ${
                otherSelected
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50 bg-card'
              }`}
            >
              <div className="w-20 h-20 rounded-md border border-dashed border-border bg-background flex items-center justify-center flex-shrink-0">
                <Icon name="MessageSquare" size={24} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">Autre modèle, nous contacter</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Vous ne trouvez pas le modèle souhaité ? Nous vous établissons un devis personnalisé.
                </p>
              </div>
              <Icon
                name={otherSelected ? 'CheckCircle' : 'ChevronRight'}
                size={22}
                className={otherSelected ? 'text-primary flex-shrink-0' : 'text-muted-foreground flex-shrink-0'}
              />
            </button>
          </div>

          <div className="flex justify-end p-5 border-t border-border bg-muted/20">
            <Button variant="default" size="sm" onClick={closeMaterialPicker}>
              Fermer
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Produit fourni par le client : désactivé pour le moment
      <ProductDetailsPopup />
      */}
      <MaterialPickerPopup />

      <div className="bg-card rounded-lg shadow-md p-6 lg:p-8">
        <div className="flex items-start gap-6  ">
          <div>
            {services.length > 1 ? (
              <>
                <h2 className="text-2xl font-semibold text-foreground mb-2">Solutions proposées</h2>
                <p className="text-muted-foreground">
                  Voici les interventions les plus adaptées à votre situation
                </p>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-semibold text-foreground mb-2">Solution proposée</h2>
                <p className="text-muted-foreground">
                  Voici l&apos;intervention la plus adaptée à votre situation.
                </p>
              </>
            )}
          </div>
        </div>
        <div className="flex justify-between pt-6 border-t border-border" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {services.map((service) => {
            // Produit fourni par le client : désactivé pour le moment
            // const providesProduct = clientProvidesProduct[service.id] || false;
            // const displayPrice = getServiceProductPrice(service, providesProduct);
            // const hasProductDetails = serviceHasProductDetails(service);
            // const thumbUrl = `${service.image_produit || ''}`.trim();
            const isFixedPrice = isFixedPriceService(service);
            const serviceMaterials = getServiceMaterials(service);
            const otherMaterialSelected = isOtherMaterialSelected(service);
            const material = otherMaterialSelected ? null : getMaterialForService(service);
            const prixSansMateriel = getServicePriceWithoutMaterial(service);
            const materialPrice = material ? Number(material.prix) || 0 : 0;
            const displayPrice = prixSansMateriel + materialPrice;

            return (
            <div
              key={service.id}
              role="button"
              tabIndex={0}
              onClick={() => handleServiceSelect(service)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleServiceSelect(service);
                }
              }}
              className={`text-left p-6 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                selectedService?.id === service.id
                  ? 'border-primary bg-primary/5 shadow-md'
                  : 'border-border hover:border-primary/50 bg-card'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      selectedService?.id === service.id ? 'bg-primary/20' : 'bg-muted'
                    }`}
                  >
                    <Icon
                      name={service.icon}
                      size={20}
                      color={selectedService?.id === service.id ? 'var(--color-primary)' : 'currentColor'}
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{service.name}</h3>
                  </div>
                </div>
                {selectedService?.id === service.id && (
                  <Icon name="CheckCircle" size={24} color="var(--color-primary)" />
                )}
              </div>

              <div className="space-y-2 mb-4">
                {service.features?.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Icon name="Check" size={16} color="var(--color-success)" />
                    <span className="text-xs text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Produit fourni par le client : désactivé pour le moment
              {service.produit_fournissable && (
                <div
                  className="mb-4 p-3 rounded-lg bg-muted/50 border border-border space-y-3"
                  onClick={(e) => e.stopPropagation()}
                >
                  {hasProductDetails ? (
                    <button
                      type="button"
                      onClick={(e) => openProductPopup(service, e)}
                      className="w-full text-left rounded-md p-2 -m-1 hover:bg-background/80 transition-colors group"
                      title="Voir le détail du matériel"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-foreground">
                          Matériel fourni
                        </span>
                        <Icon
                          name="Info"
                          size={14}
                          className="text-muted-foreground group-hover:text-primary transition-colors"
                        />
                      </div>
                      {thumbUrl ? (
                        <div className="w-16 h-16 rounded-md overflow-hidden border border-border bg-background flex items-center justify-center">
                          <img
                            src={thumbUrl}
                            alt=""
                            className="max-h-full max-w-full object-contain"
                            onError={(e) => {
                              e.target.src = '/placeholder-image.jpg';
                            }}
                          />
                        </div>
                      ) : (
                        <div className="w-16 h-16 rounded-md border border-dashed border-border bg-background flex items-center justify-center">
                          <Icon name="Package" size={20} className="text-muted-foreground" />
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">
                        Appuyez pour voir la description
                      </p>
                    </button>
                  ) : null}

                  <Checkbox
                    label="Je fournis le produit"
                    description="Cochez si vous fournissez vous-même le produit nécessaire"
                    checked={providesProduct}
                    onChange={(e) =>
                      handleClientProvidesProductChange(service, e.target.checked, e)
                    }
                  />
                </div>
              )}
              */}

              {serviceMaterials.length > 0 && (
                <div
                  className="mb-4 p-3 rounded-lg bg-muted/50 border border-border"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <p className="text-sm font-medium text-foreground mb-2">Matériel sélectionné</p>
                  {otherMaterialSelected ? (
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-16 rounded-md border border-dashed border-border bg-background flex items-center justify-center flex-shrink-0">
                      <Icon name="MessageSquare" size={20} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">Autre modèle, nous contacter</p>
                      <p className="text-xs text-muted-foreground">Prix établi sur devis</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setMaterialPickerServiceId(service.id)}
                    >
                      Changer
                    </Button>
                  </div>
                  ) : (
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-16 rounded-md overflow-hidden border border-border bg-background flex items-center justify-center flex-shrink-0">
                      {material?.image_url ? (
                        <img
                          src={material.image_url}
                          alt={material.nom}
                          className="max-h-full max-w-full object-contain"
                          onError={(e) => {
                            e.target.src = '/placeholder-image.jpg';
                          }}
                        />
                      ) : (
                        <Icon name="Package" size={20} className="text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {material?.nom || 'Aucun matériel'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        + {materialPrice.toFixed(2)} € HT
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setMaterialPickerServiceId(service.id)}
                    >
                      Changer
                    </Button>
                  </div>
                  )}
                </div>
              )}

              {isFixedPrice && !otherMaterialSelected ? (
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-foreground">
                      {Number(displayPrice).toFixed(2)} €
                    </span>
                    <span className="text-sm text-muted-foreground">HT</span>
                  </div>
                  {material && (
                    <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                      <div className="flex justify-between gap-2">
                        <span>Prestation (sans matériel)</span>
                        <span>{prixSansMateriel.toFixed(2)} €</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="truncate">Matériel : {material.nom}</span>
                        <span className="whitespace-nowrap">+ {materialPrice.toFixed(2)} €</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-warning/10 text-warning rounded-full text-sm font-medium mb-2">
                    <Icon name="FileText" size={16} />
                    <span>Sur devis</span>
                  </div>
                  {service.note && (
                    <p className="text-xs text-muted-foreground italic">{service.note}</p>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>

        {error && (
          <div className="mt-6 flex items-center gap-3 p-4 bg-error/10 border border-error/20 rounded-lg">
            <Icon name="AlertCircle" size={20} color="var(--color-error)" />
            <span className="text-sm font-medium text-error">{error}</span>
          </div>
        )}

        <StepActions isActive={isActive} showBack={showBack} className="flex justify-between pt-8 mt-8 border-t border-border">
          <Button variant="outline" size="lg" iconName="ArrowLeft" onClick={onBack}>
            Retour
          </Button>
          <Button
            variant="default"
            size="lg"
            iconName="ArrowRight"
            iconPosition="right"
            onClick={handleSubmit}
            disabled={!selectedService}
          >
            Continuer
          </Button>
        </StepActions>
      </div>
    </div>
  );
};

export default ServiceStep;
